import { useSeo } from '../lib/seo';
/**
 * /awards/:year — read-only showcase of a published cycle's winners.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Trophy } from 'lucide-react';
import api from '../lib/api';

export default function AwardsYearPage() {
  const { year } = useParams();
  const [cycle, setCycle] = useState(null);
  useSeo(
    cycle && !cycle.notFound ? `${cycle.cycle?.name || `Tourism Awards ${year}`} — Winners | Go Leicestershire` : `Tourism Awards ${year} | Go Leicestershire`,
    `Winners and highlights from the Leicestershire Tourism Awards ${year}.`
  );

  useEffect(() => {
    api.get(`/awards/${year}`).then((r) => setCycle(r.data)).catch(() => setCycle({ notFound: true }));
    window.scrollTo(0, 0);
  }, [year]);

  if (!cycle) return <div className="py-32 text-center text-muted">Loading...</div>;
  if (cycle.notFound) return (
    <div className="max-w-[1100px] mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-4xl">No awards for {year}</h1>
      <Link to="/awards" className="text-heritage mt-4 inline-block">← Current awards</Link>
    </div>
  );

  const winnersByCategory = (cycle.winners || []).reduce((acc, w) => {
    (acc[w.category_id] = acc[w.category_id] || []).push(w);
    return acc;
  }, {});

  return (
    <div>
      <section className="relative bg-charcoal text-base py-20 overflow-hidden">
        <div className="absolute inset-0 grain"/>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 relative">
          <Link to="/awards" className="inline-flex items-center gap-2 text-base/70 hover:text-mustard mb-6"><ArrowLeft size={16}/> Current awards</Link>
          <div className="label text-mustard mb-4 inline-flex items-center gap-2"><Trophy size={14}/> Winners {cycle.year}</div>
          <h1 className="font-display text-5xl lg:text-7xl leading-[0.92] mb-6">{cycle.name}</h1>
          {cycle.description && <p className="text-xl text-base/80 max-w-2xl">{cycle.description}</p>}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {cycle.categories.map((c) => {
            const winners = winnersByCategory[c.id] || [];
            if (winners.length === 0) return null;
            return (
              <div key={c.id} className="mb-16">
                <div className="label text-heritage mb-2">Category</div>
                <h2 className="font-display text-4xl mb-8">{c.name}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {winners.map((w) => (
                    <article key={w.id} className="bg-white border-2 border-mustard rounded-3xl overflow-hidden ring-4 ring-mustard/20">
                      <div className="relative aspect-[4/3] overflow-hidden">
                        {w.hero_image ? <img src={w.hero_image} alt={w.name} className="w-full h-full object-cover"/> : <div className="w-full h-full bg-stone flex items-center justify-center"><Trophy size={48} className="text-muted/30"/></div>}
                        <span className="absolute top-4 left-4 bg-mustard text-charcoal text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1"><Trophy size={12}/> Winner</span>
                      </div>
                      <div className="p-6">
                        <h3 className="font-display text-2xl mb-1">{w.name}</h3>
                        {w.town && <div className="text-muted text-sm mb-3 inline-flex items-center gap-1"><MapPin size={12}/> {w.town}</div>}
                        {w.blurb && <p className="text-sm text-charcoal/85">{w.blurb}</p>}
                        {w.attraction_slug && (
                          <Link to={`/explore/${w.attraction_slug}`} className="mt-4 inline-block text-heritage font-semibold text-sm">View venue →</Link>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
          {(cycle.winners || []).length === 0 && <div className="text-center py-16 text-muted">Winners haven't been published yet for this cycle.</div>}
        </div>
      </section>
    </div>
  );
}
